import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, gbp, fmtDate } from "@/components/ui-bits";
import { Landmark, Loader2, RefreshCw, CheckCircle2, X, Banknote, Link2 } from "lucide-react";
import { toast } from "sonner";

export function OpenBankingCard({ propertyId, onRecorded }) {
  const [sp, setSp] = useSearchParams();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => api.get(`/properties/${propertyId}/open-banking`).then(({ data }) => setSt(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);
  useEffect(() => {
    const ob = sp.get("ob");
    if (ob === "connected") { toast.success("Bank connected — you can now sync incoming rent"); load(); }
    else if (ob === "error") { toast.error("Bank connection failed or was cancelled"); }
    if (ob) { sp.delete("ob"); setSp(sp, { replace: true }); }
    // eslint-disable-next-line
  }, []);

  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/properties/${propertyId}/open-banking/connect`); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start bank connection"); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect this bank account? Incoming rent will stop syncing automatically.")) return;
    setBusy(true);
    try { await api.delete(`/properties/${propertyId}/open-banking`); toast.success("Bank disconnected"); await load(); }
    finally { setBusy(false); }
  };
  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/open-banking/sync`);
      toast.success(`Synced ${data.scanned} incoming — ${data.auto_recorded} auto-recorded, ${data.pending} to review`);
      await load(); if (data.auto_recorded > 0) onRecorded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
      load();
    } finally { setSyncing(false); }
  };
  const confirmPending = async (pid) => {
    try { await api.post(`/open-banking/pending/${pid}/confirm`); toast.success("Rent recorded — future payments with this reference will record automatically"); await load(); onRecorded?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm"); }
  };
  const dismissPending = async (pid) => {
    try { await api.post(`/open-banking/pending/${pid}/dismiss`); toast.success("Payment dismissed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not dismiss"); }
  };

  if (!st) return null;

  if (!st.ready) {
    return (
      <Card className="p-4 mb-4 border border-slate-200" data-testid="open-banking-card">
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Landmark size={16} /> Open Banking auto-rent is not configured yet.</div>
      </Card>
    );
  }

  const connected = st.connected;
  const expired = st.status === "expired";

  return (
    <Card className="p-4 mb-4 border border-blue-200 bg-blue-50/40" data-testid="open-banking-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0"><Banknote size={18} /></div>
          <div>
            <p className="font-semibold text-slate-900 text-sm flex items-center gap-2">Auto-rent · Open Banking
              {connected && <span className="text-[11px] font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Connected</span>}
              {expired && <span className="text-[11px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Reconnect needed</span>}
              {st.env === "sandbox" && <span className="text-[11px] font-medium text-slate-600 bg-slate-200 px-2 py-0.5 rounded-full">Sandbox</span>}
            </p>
            <p className="text-xs text-slate-600 mt-0.5 max-w-xl">
              {connected
                ? <>Reads incoming payments and records rent automatically. {st.accounts?.length ? `Account: ${st.accounts.join(", ")}. ` : ""}{st.last_sync ? `Last sync ${fmtDate(st.last_sync)}. ` : "Not synced yet. "}{st.learned_refs > 0 ? `${st.learned_refs} reference${st.learned_refs === 1 ? "" : "s"} auto-matching.` : ""}</>
                : "Connect the bank account this property's rent is paid into. Confirm a new payment reference once, then matching payments record automatically."}
            </p>
            {connected && <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1" data-testid="ob-autosync-note"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Auto-syncs on its own every day — no need to sync manually.</p>}
            {expired && <p className="text-xs text-amber-700 mt-1">{st.last_error || "Consent expired — banks require reconnection every 90 days."}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {connected ? (
            <>
              <button onClick={sync} disabled={syncing} data-testid="ob-sync-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-60">{syncing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Sync now</button>
              <button onClick={disconnect} disabled={busy} data-testid="ob-disconnect-btn" className="text-xs text-slate-500 hover:text-red-600 px-2 py-1.5">Disconnect</button>
            </>
          ) : (
            <button onClick={connect} disabled={busy} data-testid="ob-connect-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-60">{busy ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />} {expired ? "Reconnect bank" : "Connect bank account"}</button>
          )}
        </div>
      </div>

      {st.pending?.length > 0 && (
        <div className="mt-3 border-t border-blue-200 pt-3" data-testid="ob-pending-list">
          <p className="text-xs font-semibold text-slate-700 mb-2">{st.pending.length} incoming payment{st.pending.length === 1 ? "" : "s"} to review</p>
          <div className="space-y-2">
            {st.pending.map((p) => (
              <div key={p.pending_id} data-testid={`ob-pending-${p.pending_id}`} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{gbp(p.amount)} <span className="font-normal text-slate-500">· {p.reference || p.description || "No reference"}</span></p>
                  <p className="text-[11px] text-slate-400">{fmtDate(p.date)}</p>
                </div>
                <button onClick={() => confirmPending(p.pending_id)} data-testid={`ob-confirm-${p.pending_id}`} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1.5 text-xs font-semibold"><CheckCircle2 size={13} /> It's rent</button>
                <button onClick={() => dismissPending(p.pending_id)} data-testid={`ob-dismiss-${p.pending_id}`} className="inline-flex items-center gap-1 rounded-md border border-slate-300 text-slate-500 hover:bg-slate-50 px-2.5 py-1.5 text-xs font-medium"><X size={13} /> Not rent</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

export default OpenBankingCard;
